import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArchiveRestore, Trash2 } from "lucide-react";

import { useAppSettings } from "@/components/app-settings/app-settings-provider";
import { useAuth } from "@/components/auth/auth-provider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { apiDelete, apiGet, apiPost } from "@/lib/client/api";
import { formatRmb } from "@/lib/pricing/format";
import { workbenchRoutes } from "@/lib/routes/workbench-routes";

type QuotationListRow = {
  id: string;
  quotationNo: string;
  displayTitle: string | null;
  version: number;
  status: string;
  projectTitle: string;
  languageMode: string;
  validUntil: number | null;
  publicToken: string | null;
  grandTotalMinor: number | null;
  deletedAt: number | null;
  updatedAt: number;
  capabilities: {
    canView: boolean;
    canEdit: boolean;
    canManageLifecycle: boolean;
  };
};

type QuotationsResponse = {
  quotations: QuotationListRow[];
};

export function QuotationsPage() {
  const { language, t } = useAppSettings();
  const { user } = useAuth();
  const text = language === "zh" ? zh : en;
  const [quotations, setQuotations] = useState<QuotationListRow[]>([]);
  const [showTrash, setShowTrash] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mutatingId, setMutatingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void loadQuotations(showTrash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTrash, language]);

  async function loadQuotations(trash: boolean) {
    setLoading(true);
    setError(null);

    try {
      const response = await apiGet<QuotationsResponse>(trash ? "/api/quotations?trash=1" : "/api/quotations");
      setQuotations(response.quotations);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t("quotations.loadError"));
    } finally {
      setLoading(false);
    }
  }

  async function mutateQuotation(quotation: QuotationListRow, action: "trash" | "restore" | "permanent") {
    const confirmMessage = action === "permanent" ? text.confirmPermanent : action === "trash" ? text.confirmTrash : null;

    if (confirmMessage && !window.confirm(confirmMessage)) {
      return;
    }

    setMutatingId(quotation.id);
    setError(null);

    try {
      if (action === "trash") {
        await apiDelete<{ ok: true }>(`/api/quotations/${quotation.id}`);
      } else if (action === "restore") {
        await apiPost<Record<string, never>, { ok: true }>(`/api/quotations/${quotation.id}/restore`, {});
      } else {
        await apiDelete<{ ok: true }>(`/api/quotations/${quotation.id}/permanent`);
      }

      await loadQuotations(showTrash);
    } catch (mutationError) {
      setError(mutationError instanceof Error ? mutationError.message : text.actionError);
    } finally {
      setMutatingId(null);
    }
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t("quotations.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("quotations.description")}</p>
        </div>
        <Link
          to={workbenchRoutes.newQuotation}
          className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
        >
          {t("dashboard.newQuote")}
        </Link>
      </div>
      <div className="flex gap-2">
        <Button type="button" variant={showTrash ? "secondary" : "primary"} className="h-8 px-3" onClick={() => setShowTrash(false)}>
          {text.active}
        </Button>
        {user?.accountType === "internal" ? <Button type="button" variant={showTrash ? "primary" : "secondary"} className="h-8 px-3" onClick={() => setShowTrash(true)}>{text.trash}</Button> : null}
      </div>
      {error ? <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div> : null}
      <Card className="overflow-hidden">
        <div className="grid grid-cols-[1.2fr_1.1fr_0.7fr_0.8fr_0.7fr_160px] border-b border-border bg-muted px-4 py-2 text-xs font-medium text-muted-foreground">
          <span>{text.quotation}</span>
          <span>{text.project}</span>
          <span>{text.status}</span>
          <span>{text.total}</span>
          <span>{text.clientLink}</span>
          <span>{text.actions}</span>
        </div>
        {loading ? <div className="px-4 py-10 text-center text-sm text-muted-foreground">{t("quotations.loading")}</div> : null}
        {!loading && quotations.length === 0 ? <div className="px-4 py-10 text-center text-sm text-muted-foreground">{t("quotations.empty")}</div> : null}
        {!loading
          ? quotations.map((quotation) => (
              <div
                key={quotation.id}
                className="grid grid-cols-[1.2fr_1.1fr_0.7fr_0.8fr_0.7fr_160px] items-center border-b border-border px-4 py-3 text-sm transition last:border-b-0 hover:bg-muted"
              >
                <Link to={quotation.capabilities.canEdit ? workbenchRoutes.quotationEdit(quotation.id) : workbenchRoutes.quotationPreview(quotation.id)} className="min-w-0 font-medium text-primary hover:underline">
                  <span className="block truncate">{quotation.displayTitle || quotation.quotationNo}</span>
                  <span className="block truncate text-xs font-normal text-muted-foreground">{quotation.quotationNo}</span>
                </Link>
                <span className="truncate text-muted-foreground">{quotation.projectTitle}</span>
                <span>
                  <Badge>{getStatusLabel(quotation.status)}</Badge>
                </span>
                <span>{formatRmb(quotation.grandTotalMinor ?? 0)}</span>
                <span className="text-muted-foreground">{quotation.publicToken ? t("quotations.linkReady") : t("quotations.linkNone")}</span>
                <span className="flex gap-1">
                  {!quotation.capabilities.canManageLifecycle ? <span className="text-xs text-muted-foreground">{text.viewOnly}</span> : showTrash ? (
                    <>
                      <Button type="button" variant="secondary" className="h-8 px-2" onClick={() => void mutateQuotation(quotation, "restore")} disabled={mutatingId === quotation.id}>
                        <ArchiveRestore className="h-4 w-4" aria-hidden="true" />
                        {text.restore}
                      </Button>
                      <Button type="button" variant="secondary" className="h-8 px-2 text-destructive" onClick={() => void mutateQuotation(quotation, "permanent")} disabled={mutatingId === quotation.id}>
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </>
                  ) : (
                    <Button type="button" variant="secondary" className="h-8 px-2 text-destructive" onClick={() => void mutateQuotation(quotation, "trash")} disabled={mutatingId === quotation.id}>
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                      {text.delete}
                    </Button>
                  )}
                </span>
              </div>
            ))
          : null}
      </Card>
    </section>
  );
}

const en = {
  active: "Active quotes",
  trash: "Trash",
  quotation: "Quotation",
  project: "Project",
  status: "Status",
  total: "Total",
  clientLink: "Client Link",
  actions: "Actions",
  delete: "Delete",
  restore: "Restore",
  confirmTrash: "Move this quotation to trash?",
  confirmPermanent: "Permanently delete this quotation? This cannot be undone.",
  actionError: "Unable to update quotation.",
  viewOnly: "View only",
};

const zh: typeof en = {
  active: "报价单",
  trash: "回收站",
  quotation: "报价",
  project: "项目",
  status: "状态",
  total: "总计",
  clientLink: "客户链接",
  actions: "操作",
  delete: "删除",
  restore: "恢复",
  confirmTrash: "确认将此报价单移入回收站？",
  confirmPermanent: "确认永久删除此报价单？此操作不可恢复。",
  actionError: "无法更新报价单。",
  viewOnly: "仅查看",
};

function getStatusLabel(status: string): string {
  return status
    .split("_")
    .map((part) => part.slice(0, 1).toUpperCase() + part.slice(1))
    .join(" ");
}
